import { Organization, Role, User } from '../../types';

type CanPerformActionWhileDndOptions = {
  currentUser?: User;
  currentOrganization?: Organization;
  isDelegationRoleUnavailableEnabled: boolean;
  rolesMetadataFetched: boolean;
  roles: Role[];
  allowWhenDelegationDisabled?: boolean;
};

type CanRoleHaveUnavailableOwnerOptions = {
  currentOrganization?: Organization;
  isDelegationRoleUnavailableEnabled: boolean;
  rolesMetadataFetched: boolean;
  roles: Role[];
};

type CheckUnavailableRolesCoreOptions = {
  currentOrganization?: Organization;
  isDelegationRoleUnavailableEnabled: boolean;
  rolesMetadataFetched: boolean;
  roles: Role[];
};

/**
 * Core logic for checking if unavailable roles are allowed based on
 * organization configuration and role permissions.
 */
function checkUnavailableRolesCore({
  currentOrganization,
  isDelegationRoleUnavailableEnabled,
  rolesMetadataFetched,
  roles,
}: CheckUnavailableRolesCoreOptions): boolean {
  if (!isDelegationRoleUnavailableEnabled) {
    return false;
  }

  const allowUnavailableRoles = currentOrganization?.allowUnavailableRoles;

  if (allowUnavailableRoles === 'all') {
    return true;
  }

  if (allowUnavailableRoles === 'individual') {
    if (!rolesMetadataFetched) return false;

    const disallowedRoles = roles.filter((role) => role.allowOwnerOnDnd !== true);
    return disallowedRoles.length === 0;
  }

  return false;
}

/**
 * Determines if a user can perform an action related to DND/unavailability
 * based on organization configuration and role permissions.
 *
 * @param options - Configuration options for checking availability
 * @returns boolean indicating if action can be performed
 */
export function canPerformActionWhileDnd({
  currentUser,
  currentOrganization,
  isDelegationRoleUnavailableEnabled,
  rolesMetadataFetched,
  roles,
  allowWhenDelegationDisabled = false,
}: CanPerformActionWhileDndOptions): boolean {
  if (!currentUser) return false;

  if (!isDelegationRoleUnavailableEnabled) {
    return allowWhenDelegationDisabled;
  }

  return checkUnavailableRolesCore({
    currentOrganization,
    isDelegationRoleUnavailableEnabled,
    rolesMetadataFetched,
    roles,
  });
}

/**
 * Determines if a user can be unavailable (go on DND) based on organization
 * configuration and owned role permissions.
 *
 * @param options - Configuration options for checking availability
 * @returns boolean indicating if user can be unavailable
 */
export function canUserBeUnavailable(options: CanPerformActionWhileDndOptions): boolean {
  const allowUnavailableRoles = options.currentOrganization?.allowUnavailableRoles;
  const isOrgSettingEnabled =
    allowUnavailableRoles === 'all' || allowUnavailableRoles === 'individual';

  if (!options.isDelegationRoleUnavailableEnabled || !isOrgSettingEnabled) {
    return options.roles.length === 0;
  }

  return canPerformActionWhileDnd({
    ...options,
    allowWhenDelegationDisabled: false,
  });
}

/**
 * Determines if a role can have an unavailable owner based on organization
 * configuration and role permissions. This is used when selecting users to
 * assign to a role, to check if DND users can be selected.
 *
 * @param options - Configuration options for checking role capability
 * @returns boolean indicating if the role can have an unavailable owner
 */
export function canRoleHaveUnavailableOwner(options: CanRoleHaveUnavailableOwnerOptions): boolean {
  return checkUnavailableRolesCore(options);
}
